import { useState } from "react";
import { Plus, Trash2, Mail, Eye } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, Loader, EmptyState } from "@/components/common/Loader";
import { Field, TextInput } from "@/components/common/Forms";
import { api, errMsg, useFetch } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { NEWS_LINKS } from "@/lib/guide";

function NewsForm({ open, onOpenChange, onSaved }) {
  const [f, setF] = useState({ title: "", body: "", link: "none", email: true });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (f.title.trim().length < 2 || f.body.trim().length < 2) return toast.error("Inserisci titolo e descrizione");
    setBusy(true);
    try {
      const r = await api.post("/admin/news", { title: f.title, body: f.body, link_path: f.link === "none" ? "" : f.link, send_email: f.email });
      toast.success(f.email ? `Novità pubblicata, email inviata a ${r.data.emailed_to} centri` : "Novità pubblicata");
      setF({ title: "", body: "", link: "none", email: true });
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ivory-card rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="news-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-normal">Nuova novità</DialogTitle>
          <DialogDescription>I centri la vedranno al prossimo accesso e, se vuoi, la riceveranno via email.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <Field label="Titolo *"><TextInput data-testid="news-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Es. Ora puoi scaricare i loghi in alta qualità" /></Field>
          <Field label="Cos'è e come si usa *">
            <Textarea data-testid="news-body-input" rows={6} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="bg-ivory-deep rounded-xl" placeholder="Spiega in poche righe la novità e come provarla" />
          </Field>
          <Field label="Pulsante «Provala ora» porta a">
            <Select value={f.link} onValueChange={(v) => setF({ ...f, link: v })}>
              <SelectTrigger data-testid="news-link-select" className="bg-ivory-deep rounded-xl h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{NEWS_LINKS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <label className="flex items-center justify-between gap-4 rounded-xl bg-ivory-deep border border-warm/50 px-4 py-3 text-sm">
            <span>Invia email a tutti i centri attivi</span>
            <Switch data-testid="news-email-switch" checked={f.email} onCheckedChange={(v) => setF({ ...f, email: v })} />
          </label>
          <button data-testid="news-submit-btn" disabled={busy} onClick={submit} className="btn-gold w-full">{busy ? "Pubblicazione…" : "Pubblica novità"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewsRow({ n, onChanged }) {
  const del = async () => {
    if (!window.confirm(`Eliminare la novità "${n.title}"?`)) return;
    try {
      await api.delete(`/admin/news/${n.id}`);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid={`admin-news-${n.id}`} className="lux-card p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-serif text-2xl">{n.title}</p>
          <p className="text-sm text-warm-text mt-2 whitespace-pre-wrap leading-relaxed">{n.body}</p>
        </div>
        <button data-testid={`admin-news-delete-${n.id}`} onClick={del} aria-label="Elimina" className="text-warm-text hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.14em] text-warm-text">
        <span>{fmtDateTime(n.created_at)}</span>
        <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-gold" /> Email a {n.emailed_to} centri</span>
        <span data-testid={`admin-news-seen-${n.id}`} className="inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5 text-gold" /> Vista da {n.seen_count} su {n.center_users}</span>
      </div>
    </div>
  );
}

export default function AdminNews() {
  const { data, loading, reload } = useFetch("/admin/news");
  const [open, setOpen] = useState(false);
  return (
    <div>
      <PageHeader eyebrow="Comunicazioni" title="Novità" subtitle="Annuncia le nuove funzioni: i centri ricevono un'email e, al primo accesso, una spiegazione nell'app."
        actions={<button data-testid="news-new-btn" onClick={() => setOpen(true)} className="btn-gold"><Plus className="w-4 h-4" /> Nuova novità</button>} />
      {loading ? <Loader /> : !data?.length ? <EmptyState title="Nessuna novità pubblicata" text="Quando aggiungi una nuova funzione, annunciala da qui." />
        : <div className="space-y-4">{data.map((n) => <NewsRow key={n.id} n={n} onChanged={reload} />)}</div>}
      <NewsForm open={open} onOpenChange={setOpen} onSaved={() => { setOpen(false); reload(); }} />
    </div>
  );
}
